import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { StatusTimeline } from "@/components/shared/Records";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const ST = { enviada: ["pendiente", "En tránsito"], recibida: ["activo", "Recibida"], anulada: ["bloqueado", "Anulada"] };

const TransferDialog = ({ id, onClose, onChanged }) => {
  const { can } = useAuth();
  const [t, setT] = useState(null);
  const [got, setGot] = useState({});
  const [note, setNote] = useState("");
  useEffect(() => { if (id) api.get(`/abastecimiento/transfers/${id}`).then((r) => { setT(r.data); setGot(Object.fromEntries(r.data.items.map((i) => [i.product_id, String(i.qty_sent)]))); setNote(""); }); else setT(null); }, [id]);
  const receive = async () => {
    try { const { data } = await api.post(`/abastecimiento/transfers/${id}/receive`, { note, items: t.items.map((i) => ({ product_id: i.product_id, qty_received: parseFloat(got[i.product_id]) || 0 })) }); setT(data); toast.success("Recepción confirmada"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async () => {
    try { const { data } = await api.post(`/abastecimiento/transfers/${id}/cancel`, { reason: note }); setT(data); toast.success("Transferencia anulada"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const pending = t?.status === "enviada" && can("abastecimiento.transferir");
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="transfer-dialog">
        {t && <>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><span className="font-mono">{t.code}</span><StatusBadge testid="transfer-status-badge" status={ST[t.status][0]} label={ST[t.status][1]} /></DialogTitle></DialogHeader>
          <div className="text-sm text-[#5b6656]">{t.from_code} {t.from_name} → <b>{t.to_code} {t.to_name}</b> · enviada por {t.sent_by_name} {fmtDateTime(t.sent_at)}</div>
          <table className="w-full text-sm"><thead className="text-xs bg-[#f7f9f5]"><tr><th className="px-3 py-2 text-left">Producto</th><th className="px-3 text-right">Enviado</th><th className="px-3 text-right">Recibido</th></tr></thead>
            <tbody>{t.items.map((i) => (
              <tr key={i.product_id} className="border-b border-[#f0f2ee]"><td className="px-3 py-2">{i.name}<div className="text-[11px] font-mono text-[#8a9584]">{i.sku}</div></td>
                <td className="px-3 text-right">{i.qty_sent} {i.unit}</td>
                <td className="px-3 text-right">{pending ? <Input data-testid={`receive-qty-${i.product_id}`} type="number" min="0" step="any" className="w-24 h-8 ml-auto" value={got[i.product_id]} onChange={(e) => setGot({ ...got, [i.product_id]: e.target.value })} /> : i.qty_received ?? "—"}</td></tr>))}</tbody></table>
          {t.differences?.length > 0 && <div className="text-xs text-[#b42318]" data-testid="transfer-differences">Diferencias: {t.differences.join("; ")}</div>}
          {pending && <div className="space-y-3">
            <Field label="Observación" hint="Obligatoria si hay diferencias o para anular."><Textarea data-testid="transfer-note-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
            <div className="flex flex-wrap gap-2"><Button data-testid="transfer-receive-btn" className="rounded-full" onClick={receive}>Confirmar recepción</Button>
              <Button data-testid="transfer-cancel-btn" variant="outline" className="rounded-full" onClick={cancel}>Anular (devuelve al origen)</Button></div>
          </div>}
          <StatusTimeline items={t.history} labels={Object.fromEntries(Object.entries(ST).map(([k, v]) => [k, v[1]]))} />
        </>}
      </DialogContent>
    </Dialog>
  );
};

export default function Transfers() {
  const [status, setStatus] = useState("enviada");
  const [list, setList] = useState([]);
  const [view, setView] = useState(null);
  const load = () => api.get("/abastecimiento/transfers", { params: { status } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="transfers-page">
      <PageHeader eyebrow="Abastecimiento" title="Transferencias" subtitle="Para enviar, entra a la bodega de origen y usa Transferir. El destino confirma lo recibido." />
      <div className="flex flex-wrap gap-2 mb-4">{[["enviada", "En tránsito"], ["recibida", "Recibidas"], ["anulada", "Anuladas"], ["", "Todas"]].map(([k, l]) => (
        <Button key={k || "all"} size="sm" data-testid={`transfers-filter-${k || "all"}`} variant={status === k ? "default" : "outline"} className="rounded-full" onClick={() => setStatus(k)}>{l}</Button>))}</div>
      <div className="syc-card overflow-hidden">
        {list.length === 0 ? <EmptyState title="Sin transferencias" /> : list.map((t) => (
          <button key={t.id} data-testid={`transfer-row-${t.id}`} onClick={() => setView(t.id)} className="w-full flex flex-wrap items-center gap-3 px-5 py-3 border-b border-[#f0f2ee] hover:bg-[#f6fbf2] text-sm text-left">
            <span className="font-mono text-xs w-28">{t.code}</span>
            <span className="flex-1 min-w-[200px]"><b>{t.from_code}</b> → <b>{t.to_code}</b> <span className="text-xs text-[#5b6656]">· {t.items.length} productos</span></span>
            <span className="text-xs text-[#5b6656]">{fmtDateTime(t.sent_at)}</span>
            <StatusBadge status={ST[t.status][0]} label={ST[t.status][1]} />
          </button>))}
      </div>
      <TransferDialog id={view} onClose={() => setView(null)} onChanged={load} />
    </div>
  );
}
